// Statistics dashboard — the always-Approved reporting dashboard.
//
// ⚠️ CROSS-REFERENCE: the category / state / trend / by-shelter aggregation below is
// mirrored (Approved-only) in src/services/statisticsReport.js, which powers the
// "Statistics Summary" Standard Report PDF. If you change the math here, update there.
//
// Split out of ReportsPage on 2 Sep 2026 (branch jkw/20260902-fixes-and-statistics):
// the CSV builder (top of the old combined screen) stayed in ReportsPage; this file
// is the standalone Statistics screen (Manage → Statistics). The two are data-
// independent — this dashboard is scoped ONLY by its own Year + Period controls and
// always shows Approved (committed) spend. See docs/reports-module.md.

import { useEffect, useState } from "react";
import { LIST_LIMIT, listAllVisibleRequests } from "../../data/requests";
import { listResponderTasks } from "../../data/requestTasks";
import { REQUEST_STATUSES } from "../../utils/enums";
import { calcUniItems, STATUS_CONFIG } from "../../utils/formConstants";
import { decisionMatch, isStationeryFee, requestDecisionTotals } from "../../utils/helpers";
import { averageResponseHours } from "../../utils/responseTime";

// Thousands-separated dollars, no cents — e.g. money(54833) === "$54,833".
function money(n) {
  return `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

// Like money() but keeps cents — e.g. money2(20135.5) === "$20,135.50".
function money2(n) {
  return `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Date window [startISO, endISO] (inclusive) for the reporting-period control, or
// null for no restriction. Financial Year = Australian 1 Jul – 30 Jun (labelled by
// the ending year: 2026 = 1 Jul 2025 – 30 Jun 2026). Last 12 Months is a rolling
// window from today and ignores the year.
function periodWindow(year, type, customFrom, customTo) {
  if (type === "Other") {
    // Custom date range (the "Other" period) — either bound is optional; an open
    // end is treated as unbounded. null when neither is set.
    if (!customFrom && !customTo) return null;
    return [customFrom || "0000-01-01", customTo || "9999-12-31"];
  }
  if (type === "Last 12 Months") {
    const iso = (d) => d.toISOString().slice(0, 10);
    const end = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - 1);
    return [iso(start), iso(end)];
  }
  if (year === "All") return null;
  const y = parseInt(year, 10);
  if (Number.isNaN(y)) return null;
  if (type === "Calendar Year") return [`${y}-01-01`, `${y}-12-31`];
  return [`${y - 1}-07-01`, `${y}-06-30`];
}

// Fixed top-down order for the "Requests by Status" panel.
const STATUS_ORDER = [
  REQUEST_STATUSES.DRAFT,
  REQUEST_STATUSES.SUBMITTED,
  REQUEST_STATUSES.PART_PROCESSED,
  REQUEST_STATUSES.FULLY_PROCESSED,
];

// Display labels for the By Category panel (key -> label). "laptops" shows as "Devices".
const CAT_LABELS = {
  fees: "Fees",
  uniforms: "Uniforms",
  shoes: "Shoes",
  laptops: "Devices",
  stationery: "Stationery",
};

function StatCard({ label, value, sub, colour }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${"#CFE0DE"}`,
        borderRadius: 12,
        padding: "9px 12px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 800, color: colour || "#00897B" }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1A2E2B", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: "#607D79", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// s = { state, count, children, fees, uniforms, shoes, devices, stationery, value } — every
// figure is pre-calculated once per service in StatisticsPage (shelterStats), so the table can
// sort on it. Fees excludes stationery (own column since 12 Oct 2026), so the $ columns add up
// to Total $.
// "By DFV Service Approved" columns. `text` columns sort A-Z first; number columns
// sort high-to-low first. Clicking the active column again reverses it.
const SHELTER_COLS = [
  { label: "DFV Service", key: "name", text: true },
  { label: "State", key: "state", text: true },
  { label: "Requests", key: "count" },
  { label: "Children", key: "children" },
  { label: "Fees $", key: "fees" },
  { label: "Uniforms $", key: "uniforms" },
  { label: "Shoes $", key: "shoes" },
  { label: "Devices $", key: "devices" },
  { label: "Stationery $", key: "stationery" },
  { label: "Total $", key: "value" },
];

const byText = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });

// entries = Object.entries(shelterStats) -> [[name, stats], ...]. Ties always fall back to
// the service name A-Z so the order never jumps about.
function sortShelterEntries(entries, key, dir) {
  const isText = key === "name" || key === "state";
  const val = (e) => (key === "name" ? e[0] : e[1][key]);
  const sign = dir === "asc" ? 1 : -1;
  return [...entries].sort((a, b) => {
    const c = isText ? byText(val(a), val(b)) : val(a) - val(b);
    return c !== 0 ? c * sign : byText(a[0], b[0]);
  });
}

// A clickable column header: a real <button> inside the <th> (keyboard + screen-reader friendly),
// with an arrow showing the active sort (⬍ = not sorted by this column).
function SortTh({ col, sort, onSort }) {
  const active = sort.key === col.key;
  const ariaSort = active ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "⬍";
  return (
    <th
      aria-sort={ariaSort}
      style={{
        padding: "9px 14px",
        textAlign: "left",
        fontSize: 11,
        fontWeight: 700,
        // The column currently sorted is highlighted: teal tint, darker text, stronger underline.
        color: active ? "#00695C" : "#607D79",
        background: active ? "#E0F2F1" : undefined,
        borderBottom: `2px solid ${active ? "#00897B" : "#CFE0DE"}`,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        className="m-0 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-left"
        style={{
          font: "inherit",
          color: "inherit",
          letterSpacing: "inherit",
          textTransform: "inherit",
        }}
      >
        {col.label}
        <span
          aria-hidden="true"
          style={{ opacity: active ? 1 : 0.6, fontSize: active ? 13 : 12, fontWeight: 800 }}
        >
          {arrow}
        </span>
      </button>
    </th>
  );
}

function ShelterRow({ name, s, i }) {
  const { fees, uniforms, shoes, devices, stationery } = s;
  return (
    <tr style={{ background: i % 2 === 0 ? "#ffffff" : "#F5F7F7" }}>
      <td style={{ padding: "10px 14px", fontWeight: 700, color: "#00897B" }}>{name}</td>
      <td style={{ padding: "10px 14px" }}>
        <span
          style={{
            background: "#E0F2F1",
            color: "#00695C",
            borderRadius: 4,
            padding: "2px 8px",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {s.state}
        </span>
      </td>
      <td style={{ padding: "10px 14px" }}>{s.count}</td>
      <td style={{ padding: "10px 14px" }}>{s.children}</td>
      <td style={{ padding: "10px 14px" }}>{fees > 0 ? money2(fees) : "-"}</td>
      <td style={{ padding: "10px 14px" }}>{uniforms > 0 ? money2(uniforms) : "-"}</td>
      <td style={{ padding: "10px 14px" }}>{shoes > 0 ? money2(shoes) : "-"}</td>
      <td style={{ padding: "10px 14px" }}>{devices > 0 ? money2(devices) : "-"}</td>
      <td style={{ padding: "10px 14px" }}>{stationery > 0 ? money2(stationery) : "-"}</td>
      <td style={{ padding: "10px 14px", fontWeight: 700, color: "#00695C" }}>{money2(s.value)}</td>
    </tr>
  );
}

function TrendBar({ k, s, maxTrend }) {
  const barH = Math.round((s.value / maxTrend) * 110);
  const label = k.length === 7 ? `${k.slice(5)}/${k.slice(2, 4)}` : k;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 80,
        flex: "0 0 auto",
      }}
    >
      <div
        style={{
          width: 50,
          height: barH || 4,
          background: "#00897B",
          borderRadius: "4px 4px 0 0",
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          marginTop: 32,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: -32,
            fontSize: 11,
            fontWeight: 700,
            color: "#00897B",
            whiteSpace: "nowrap",
          }}
        >
          {s.count} request{s.count !== 1 ? "s" : ""}
        </span>
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#1A2E2B",
          marginTop: 4,
          textAlign: "center",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 10, color: "#607D79" }}>{s.children} children</div>
      <div style={{ fontSize: 11, color: "#00695C", fontWeight: 700, marginTop: 2 }}>
        {money(s.value)}
      </div>
    </div>
  );
}

function StatusBar({ status, count, totalReqs }) {
  const cfg = STATUS_CONFIG[status] || { colour: "#999", bg: "#eee" };
  const pct = totalReqs > 0 ? Math.round((count / totalReqs) * 100) : 0;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: cfg.colour }} />
        <span style={{ fontSize: 13, color: "#1A2E2B" }}>{status}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ background: "#CFE0DE", borderRadius: 4, height: 6, width: 60 }}>
          <div style={{ background: cfg.colour, borderRadius: 4, height: 6, width: `${pct}%` }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#1A2E2B", minWidth: 20 }}>
          {count}
        </span>
      </div>
    </div>
  );
}

// The Statistics screen (route: /statistics). The shared app-wide request list is
// capped at LIST_LIMIT (500), which would quietly cut a full-year view short, so the
// screen loads EVERY request itself, in pages, when it opens (12 Oct 2026). While
// loading it shows a message rather than part-finished figures. If the full load
// fails it falls back to the capped list it was given, with a warning.
function StatisticsPage({ requests }) {
  const [state, setState] = useState({ loading: true, rows: null });
  useEffect(() => {
    let active = true;
    listAllVisibleRequests().then((rows) => {
      if (active) setState({ loading: false, rows });
    });
    return () => {
      active = false;
    };
  }, []);

  if (state.loading) {
    return (
      <div style={{ padding: "24px 28px", maxWidth: "var(--page-max)", margin: "0 auto" }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#1A2E2B" }}>Statistics</h1>
        <div role="status" style={{ fontSize: 14, color: "#607D79", marginTop: 16 }}>
          Loading all requests…
        </div>
      </div>
    );
  }
  if (state.rows) return <StatisticsView requests={state.rows} />;
  return <StatisticsView requests={requests} capped />;
}

// The dashboard itself — pure over the `requests` it is given (tests render this).
function StatisticsView({ requests, capped = false }) {
  const [periodYear, setPeriodYear] = useState("All");
  const [periodType, setPeriodType] = useState("Financial Year");
  const [statsFrom, setStatsFrom] = useState(""); // custom range (Period = "Other")
  const [statsTo, setStatsTo] = useState("");
  // Sort for the By DFV Service table. Default = Total $ high-to-low.
  const [shelterSort, setShelterSort] = useState({ key: "value", dir: "desc" });
  // Responder tasks (incl. actioned) for the Response Time card.
  const [respTasks, setRespTasks] = useState([]);
  useEffect(() => {
    let active = true;
    listResponderTasks().then((rows) => {
      if (active && rows) setRespTasks(rows);
    });
    return () => {
      active = false;
    };
  }, []);

  const allRequests = requests || [];
  // Years present in the data, newest first, for the reporting-period control.
  const periodYears = [
    "All",
    ...Array.from(
      new Set(allRequests.map((r) => (r.reportDay || r.date || "").slice(0, 4)).filter(Boolean))
    )
      .sort()
      .reverse(),
  ];
  const pw = periodWindow(periodYear, periodType, statsFrom, statsTo);

  // The dashboard is scoped ONLY by the reporting-period control (+ the per-item
  // Approved filter applied in the aggregates below).
  const statsFiltered = allRequests.filter((r) => {
    // Brisbane business day (reportDay), not the legacy UTC-baked `date`.
    const rday = r.reportDay || r.date || "";
    if (pw && (rday < pw[0] || rday > pw[1])) return false;
    return true;
  });

  // Aggregate totals
  const totalReqs = statsFiltered.length;
  // "Fully Processed" is the terminal "every item decided" state — the only
  // value counted here. The legacy "Completed" status was retired entirely
  // (Part A, 26 June 2026; it is dropped from the enum in Part B), so it is
  // never produced and is not counted.
  const processedReqs = statsFiltered.filter(
    (r) => r.status === REQUEST_STATUSES.FULLY_PROCESSED
  ).length;

  // Approved-only ("committed spend") aggregates — the headline numbers on this
  // page. Each request contributes only its Approved items; a request with no
  // approved item doesn't count toward the approved request/children totals.
  const approvedAgg = statsFiltered.map((r) => requestDecisionTotals(r, "approved"));
  const approvedValue = approvedAgg.reduce((s, a) => s + a.total, 0);
  const approvedChildren = approvedAgg.reduce((s, a) => s + a.children, 0);
  const approvedReqs = approvedAgg.filter((a) => a.hasApproved).length;

  // Response Time — average business hours (08:00–18:00, overnight excluded) to
  // action a New Request task, counting only tasks received 08:00–16:45, scoped
  // to the reporting period by the task's created date.
  const respHours = averageResponseHours(
    respTasks.filter((t) => {
      if (t.type !== "new_request") return false;
      if (pw) {
        const d = (t.created_at || "").slice(0, 10);
        if (d < pw[0] || d > pw[1]) return false;
      }
      return true;
    })
  );

  // By category — items matching the decision filter (Approved by default).
  // Stationery is split OUT of fees: a stationery fee type (isStationeryFee —
  // Stationery, Stationery & Textbooks, Textbooks) counts as Stationery; every other
  // fee type counts as Fees (so the two don't double-count).
  // children = matching items of that type; count = requests with at least one.
  const catStats = {
    stationery: { count: 0, children: 0, value: 0 },
    fees: { count: 0, children: 0, value: 0 },
    uniforms: { count: 0, children: 0, value: 0 },
    shoes: { count: 0, children: 0, value: 0 },
    laptops: { count: 0, children: 0, value: 0 },
  };
  statsFiltered.forEach((r) => {
    const inReq = {
      fees: false,
      uniforms: false,
      shoes: false,
      laptops: false,
      stationery: false,
    };
    (r.children || []).forEach((c) => {
      let childFees = false;
      let childStationery = false;
      (c.fees || []).forEach((f) => {
        if (!decisionMatch(f.approvalStatus, "approved")) return;
        const amt = parseFloat(f.feeAmount) || 0;
        if (isStationeryFee(f.feeType)) {
          catStats.stationery.value += amt;
          childStationery = true;
        } else {
          catStats.fees.value += amt;
          childFees = true;
        }
      });
      if (childFees) {
        catStats.fees.children++;
        inReq.fees = true;
      }
      if (childStationery) {
        catStats.stationery.children++;
        inReq.stationery = true;
      }
      if (c.uniforms && decisionMatch(c.uniforms.approvalStatus, "approved")) {
        catStats.uniforms.value += calcUniItems([c.uniforms]);
        catStats.uniforms.children++;
        inReq.uniforms = true;
      }
      if (c.shoes && decisionMatch(c.shoes.approvalStatus, "approved")) {
        catStats.shoes.value +=
          (c.shoes.jQty || 0) * (parseFloat(c.shoes.jCost) || 0) +
          (c.shoes.lQty || 0) * (parseFloat(c.shoes.lCost) || 0);
        catStats.shoes.children++;
        inReq.shoes = true;
      }
      if (c.laptop && decisionMatch(c.laptop.approvalStatus, "approved")) {
        catStats.laptops.value += parseFloat(c.laptop.estimatedCost) || 0;
        catStats.laptops.children++;
        inReq.laptops = true;
      }
    });
    if (inReq.fees) catStats.fees.count++;
    if (inReq.uniforms) catStats.uniforms.count++;
    if (inReq.shoes) catStats.shoes.count++;
    if (inReq.laptops) catStats.laptops.count++;
    if (inReq.stationery) catStats.stationery.count++;
  });
  const maxCatValue = Math.max(...Object.values(catStats).map((c) => c.value), 1);

  // By state — approved-only (committed spend); states with no approved items are omitted.
  const stateStats = {};
  statsFiltered.forEach((r) => {
    const a = requestDecisionTotals(r, "approved");
    if (!a.hasApproved) return;
    if (!stateStats[r.state]) stateStats[r.state] = { count: 0, children: 0, value: 0 };
    stateStats[r.state].count++;
    stateStats[r.state].children += a.children;
    stateStats[r.state].value += a.total;
  });
  const maxStateValue = Math.max(...Object.values(stateStats).map((s) => s.value), 1);

  // By shelter
  const shelterStats = {};
  statsFiltered.forEach((r) => {
    const a = requestDecisionTotals(r, "approved");
    if (!a.hasApproved) return;
    if (!shelterStats[r.shelterName])
      shelterStats[r.shelterName] = {
        count: 0,
        children: 0,
        value: 0,
        state: r.state,
        fees: 0,
        uniforms: 0,
        shoes: 0,
        devices: 0,
        stationery: 0,
      };
    shelterStats[r.shelterName].count++;
    shelterStats[r.shelterName].children += a.children;
    shelterStats[r.shelterName].value += a.total;
    shelterStats[r.shelterName].fees += a.fees;
    shelterStats[r.shelterName].uniforms += a.uniforms;
    shelterStats[r.shelterName].shoes += a.shoes;
    shelterStats[r.shelterName].devices += a.laptops;
    shelterStats[r.shelterName].stationery += a.stationery;
  });

  const sortedShelters = sortShelterEntries(
    Object.entries(shelterStats),
    shelterSort.key,
    shelterSort.dir
  );

  function toggleShelterSort(col) {
    setShelterSort((cur) =>
      cur.key === col.key
        ? { key: col.key, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { key: col.key, dir: col.text ? "asc" : "desc" }
    );
  }

  // By status
  const statusStats = {};
  statsFiltered.forEach((r) => {
    statusStats[r.status] = (statusStats[r.status] || 0) + 1;
  });

  // Trend — always grouped by MONTH; the months shown are scoped by the Year +
  // Period control (statsFiltered). Approved committed spend, all categories.
  const trendStats = {};
  statsFiltered.forEach((r) => {
    const key = (r.reportDay || r.date || "unknown").slice(0, 7);
    if (!trendStats[key]) trendStats[key] = { count: 0, value: 0, children: 0 };
    const a = requestDecisionTotals(r, "approved");
    if (!a.hasApproved) return;
    trendStats[key].count++;
    trendStats[key].value += a.total;
    trendStats[key].children += a.children;
  });
  const trendKeys = Object.keys(trendStats).sort();
  const maxTrend = Math.max(...trendKeys.map((k) => trendStats[k].value), 1);

  const selectStyle =
    "py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

  const COLOURS_CAT = {
    fees: "#1565C0",
    uniforms: "#00897B",
    shoes: "#4E342E",
    laptops: "#37474F",
    stationery: "#F57F17",
  };
  const COLOURS_STATE = {
    QLD: "#00695C",
    NSW: "#1565C0",
    VIC: "#4A148C",
    WA: "#E65100",
    SA: "#B71C1C",
    TAS: "#1B5E20",
    NT: "#E65100",
    ACT: "#004D40",
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: "var(--page-max)", margin: "0 auto" }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#1A2E2B" }}>Statistics</h1>
        <div style={{ fontSize: 12, color: "#607D79", marginTop: 4 }}>
          All dates and totals are based on Brisbane (AEST) business days.
        </div>
        {/* Only when the full (uncapped) load failed and we fell back to the shared
            list, which is capped at LIST_LIMIT (finding B5; fixed 12 Oct 2026). */}
        {capped && (
          <div style={{ fontSize: 12, color: "#8a5a00", fontWeight: 600, marginTop: 6 }}>
            Couldn't load every request — showing the most recent {LIST_LIMIT} only, so totals may
            be short. Refresh the page to try again.
          </div>
        )}
      </div>

      {/* STATISTICS PERIOD — scopes the (Approved-only) dashboard */}
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 12,
          padding: "8px 16px",
          marginBottom: 22,
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#607D79",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            marginBottom: 12,
          }}
        >
          Statistics Period
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              YEAR
            </div>
            <select
              value={periodYear}
              onChange={(e) => setPeriodYear(e.target.value)}
              className={selectStyle}
              disabled={periodType === "Last 12 Months" || periodType === "Other"}
            >
              {periodYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              PERIOD
            </div>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className={selectStyle}
            >
              <option value="Financial Year">Financial Year</option>
              <option value="Calendar Year">Calendar Year</option>
              <option value="Last 12 Months">Last 12 Months</option>
              <option value="Other">Other (date range)</option>
            </select>
          </div>
          {periodType === "Other" && (
            <>
              <div>
                <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
                  FROM
                </div>
                <input
                  type="date"
                  value={statsFrom}
                  onChange={(e) => setStatsFrom(e.target.value)}
                  className={selectStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
                  TO
                </div>
                <input
                  type="date"
                  value={statsTo}
                  onChange={(e) => setStatsTo(e.target.value)}
                  className={selectStyle}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 10,
          marginBottom: 22,
        }}
      >
        <StatCard
          label="Total Requests Approved"
          value={approvedReqs}
          colour={"#00897B"}
          sub={`${processedReqs} fully processed`}
        />
        <StatCard
          label="Total Children Approved"
          value={approvedChildren}
          colour={"#1565C0"}
          sub="with approved items"
        />
        <StatCard
          label="Average Children per Approved Request"
          value={approvedReqs > 0 ? (approvedChildren / approvedReqs).toFixed(1) : "-"}
          colour={"#00695C"}
        />
        <StatCard
          label="Total Value Approved"
          value={money(approvedValue)}
          colour={"#388E3C"}
          sub="committed (approved) spend"
        />
        <StatCard
          label="Avg per Request Approved"
          value={approvedReqs > 0 ? money(approvedValue / approvedReqs) : "-"}
          colour={"#E65100"}
        />
        <StatCard
          label="Average per Child Approved"
          value={approvedChildren > 0 ? money(approvedValue / approvedChildren) : "-"}
          colour={"#5E35B1"}
          sub="approved $ per child"
        />
        <StatCard
          label="Response Time"
          value={respHours == null ? "-" : `${respHours.toFixed(1)} hrs`}
          colour={"#00838F"}
          sub="avg New Request, 8am–4:45pm"
        />
      </div>

      {/* THREE COLUMN ROW: by status | by category | by state */}
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}
      >
        {/* By Status */}
        <div
          style={{
            background: "#FFFFFF",
            border: `1px solid ${"#CFE0DE"}`,
            borderRadius: 12,
            padding: "10px 20px",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1A2E2B", marginBottom: 16 }}>
            Requests by Status
          </div>
          {Object.entries(statusStats)
            .sort(function (a, b) {
              const ia = STATUS_ORDER.indexOf(a[0]);
              const ib = STATUS_ORDER.indexOf(b[0]);
              return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
            })
            .map(([status, count]) => (
              <StatusBar key={status} status={status} count={count} totalReqs={totalReqs} />
            ))}
          {Object.keys(statusStats).length === 0 && (
            <p style={{ color: "#607D79", fontSize: 13 }}>No data</p>
          )}
        </div>

        {/* By Category */}
        <div
          style={{
            background: "#FFFFFF",
            border: `1px solid ${"#CFE0DE"}`,
            borderRadius: 12,
            padding: "10px 20px",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1A2E2B", marginBottom: 16 }}>
            By Category - Approved
          </div>
          {Object.entries(catStats).map(([cat, s]) => (
            <div key={cat} style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                  marginBottom: 4,
                }}
              >
                <span style={{ fontWeight: 600, color: "#1A2E2B" }}>{CAT_LABELS[cat] || cat}</span>
                <span style={{ color: "#607D79" }}>
                  {s.children} child{s.children !== 1 ? "ren" : ""} -{" "}
                  {s.value > 0 ? money(s.value) : "no $ value"}
                </span>
              </div>
              <div style={{ background: "#CFE0DE", borderRadius: 4, height: 8 }}>
                <div
                  style={{
                    background: COLOURS_CAT[cat] || "#00897B",
                    borderRadius: 4,
                    height: 8,
                    width:
                      s.value > 0
                        ? `${Math.round((s.value / maxCatValue) * 100)}%`
                        : s.count > 0
                          ? "15%"
                          : "0%",
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: "#607D79", marginTop: 2 }}>
                {s.count} request{s.count !== 1 ? "s" : ""}
              </div>
            </div>
          ))}
        </div>

        {/* By State */}
        <div
          style={{
            background: "#FFFFFF",
            border: `1px solid ${"#CFE0DE"}`,
            borderRadius: 12,
            padding: "10px 20px",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1A2E2B", marginBottom: 16 }}>
            By State - Approved
          </div>
          {Object.entries(stateStats)
            .sort((a, b) => b[1].value - a[1].value)
            .map(([state, s]) => (
              <div key={state} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 700, color: COLOURS_STATE[state] || "#00897B" }}>
                    {state}
                  </span>
                  <span style={{ color: "#607D79" }}>
                    {s.count} req - {s.children} children - {money(s.value)}
                  </span>
                </div>
                <div style={{ background: "#CFE0DE", borderRadius: 4, height: 8 }}>
                  <div
                    style={{
                      background: COLOURS_STATE[state] || "#00897B",
                      borderRadius: 4,
                      height: 8,
                      width: `${Math.round((s.value / maxStateValue) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          {Object.keys(stateStats).length === 0 && (
            <p style={{ color: "#607D79", fontSize: 13 }}>No data</p>
          )}
        </div>
      </div>

      {/* TREND CHART */}
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 12,
          padding: "10px 20px",
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1A2E2B", marginBottom: 16 }}>
          Monthly Trend — Requests & Value
        </div>
        {trendKeys.length > 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 8,
              minHeight: 210,
              overflowX: "auto",
              overflowY: "visible",
            }}
          >
            {trendKeys.map((k) => (
              <TrendBar key={k} k={k} s={trendStats[k]} maxTrend={maxTrend} />
            ))}
          </div>
        ) : (
          <p style={{ color: "#607D79", fontSize: 13 }}>No data for selected filters.</p>
        )}
      </div>

      {/* BY SHELTER TABLE */}
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 12,
          padding: "10px 20px",
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1A2E2B", marginBottom: 14 }}>
          By DFV Service Approved
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#F7F9F9" }}>
                {SHELTER_COLS.map((col) => (
                  <SortTh key={col.key} col={col} sort={shelterSort} onSort={toggleShelterSort} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedShelters.map(([name, s], i) => (
                <ShelterRow key={name} name={name} s={s} i={i} />
              ))}
              {/* Totals row */}
              <tr style={{ background: "#00695C", color: "#FFFFFF", fontWeight: 700 }}>
                <td style={{ padding: "10px 14px" }} colSpan={2}>
                  TOTAL
                </td>
                <td style={{ padding: "10px 14px" }}>{approvedReqs}</td>
                <td style={{ padding: "10px 14px" }}>{approvedChildren}</td>
                <td style={{ padding: "10px 14px" }}>
                  {money2(approvedAgg.reduce((s, a) => s + a.fees, 0))}
                </td>
                <td style={{ padding: "10px 14px" }}>
                  {money2(approvedAgg.reduce((s, a) => s + a.uniforms, 0))}
                </td>
                <td style={{ padding: "10px 14px" }}>
                  {money2(approvedAgg.reduce((s, a) => s + a.shoes, 0))}
                </td>
                <td style={{ padding: "10px 14px" }}>
                  {money2(approvedAgg.reduce((s, a) => s + a.laptops, 0))}
                </td>
                <td style={{ padding: "10px 14px" }}>
                  {money2(approvedAgg.reduce((s, a) => s + a.stationery, 0))}
                </td>
                <td style={{ padding: "10px 14px" }}>{money2(approvedValue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export { periodWindow, ShelterRow, StatCard, StatisticsPage, StatisticsView, StatusBar, TrendBar };
